
/**
 * Content Filter Service
 * Manages hiding reported/blocked content from the user
 */

const REPORTED_CONTENT_KEY = 'reported_content';
// const BLOCKED_USERS_KEY = 'blocked_users';
// const BLOCKED_HASHTAGS_KEY = 'blocked_hashtags';

interface ReportedContent {
    postIds: string[];
    userIds: string[];
    hashtags: string[];
    lastUpdated: number;
}

export class ContentFilterService {
    private static instance: ContentFilterService;
    private reportedContent: ReportedContent = {
        postIds: [],
        userIds: [],
        hashtags: [],
        lastUpdated: 0
    };

    private constructor() {
        this.loadReportedContent();
    }

    public static getInstance(): ContentFilterService {
        if (!ContentFilterService.instance) {
            ContentFilterService.instance = new ContentFilterService();
        }
        return ContentFilterService.instance;
    }

    /**
     * Load reported content from local storage
     */
    private async loadReportedContent(): Promise<void> {
        try {
            const stored = localStorage.getItem(REPORTED_CONTENT_KEY);
            if (stored) {
                this.reportedContent = JSON.parse(stored);
            }
        } catch (error) {
            console.error('Failed to load reported content:', error);
        }
    }

    /**
     * Save reported content to local storage
     */
    private async saveReportedContent(): Promise<void> {
        try {
            this.reportedContent.lastUpdated = Date.now();
            localStorage.setItem(REPORTED_CONTENT_KEY, JSON.stringify(this.reportedContent));
        } catch (error) {
            console.error('Failed to save reported content:', error);
        }
    }

    /**
     * Add a reported post to the filter list
     */
    public async addReportedPost(postId: string): Promise<void> {
        if (!this.reportedContent.postIds.includes(postId)) {
            this.reportedContent.postIds.push(postId);
            await this.saveReportedContent();
        }
    }

    /**
     * Add a reported user to the filter list
     */
    public async addReportedUser(userId: string): Promise<void> {
        if (!this.reportedContent.userIds.includes(userId)) {
            this.reportedContent.userIds.push(userId);
            await this.saveReportedContent();
        }
    }

    /**
     * Add a reported hashtag to the filter list
     */
    public async addReportedHashtag(hashtag: string): Promise<void> {
        const cleanHashtag = hashtag.replace('#', '').toLowerCase();
        if (!this.reportedContent.hashtags.includes(cleanHashtag)) {
            this.reportedContent.hashtags.push(cleanHashtag);
            await this.saveReportedContent();
        }
    }

    /**
     * Check if a post should be filtered out
     */
    public isPostFiltered(postId: string): boolean {
        return this.reportedContent.postIds.includes(postId);
    }

    /**
     * Check if a user should be filtered out
     */
    public isUserFiltered(userId: string): boolean {
        return this.reportedContent.userIds.includes(userId);
    }

    /**
     * Check if post hashtags contain filtered hashtags
     */
    public hasFilteredHashtags(postHashtags: string[]): boolean {
        return this.reportedContent.hashtags.some(filteredHashtag => 
            postHashtags.some(postHashtag => 
                postHashtag.toLowerCase() === filteredHashtag.toLowerCase()
            )
        );
    }

    /**
     * Filter an array of post IDs, removing reported content
     */
    public filterPostIds(postIds: string[]): string[] {
        return postIds.filter(postId => !this.isPostFiltered(postId));
    }

    /**
     * Filter an array of posts, removing reported content
     */
    public filterPosts<T extends { postid: string; userid?: string; addressid?: string; hashtags?: string[] }>(posts: T[]): T[] {
        return posts.filter(post => {
            // Filter out reported posts
            if (this.isPostFiltered(post.postid)) {
                return false;
            }
            
            // Filter out posts from reported users (check both userid and addressid)
            const authorId = post.userid || post.addressid;
            if (authorId && this.isUserFiltered(authorId)) {
                return false;
            }
            
            // Filter out posts with reported hashtags
            if (post.hashtags && this.hasFilteredHashtags(post.hashtags)) {
                return false;
            }
            
            return true;
        });
    }

    /**
     * Get all reported content (for debugging/management)
     */
    public getReportedContent(): ReportedContent {
        return { ...this.reportedContent };
    }

    /**
     * Clear all reported content (for testing/reset)
     */
    public async clearReportedContent(): Promise<void> {
        this.reportedContent = {
            postIds: [],
            userIds: [],
            hashtags: [],
            lastUpdated: 0
        };
        await this.saveReportedContent();
    }

    /**
     * Remove a post from the filter list (if user wants to unreport)
     */
    public async removeReportedPost(postId: string): Promise<void> {
        const index = this.reportedContent.postIds.indexOf(postId);
        if (index > -1) {
            this.reportedContent.postIds.splice(index, 1);
            await this.saveReportedContent();
        }
    }

    /**
     * Remove a user from the filter list
     */
    public async removeReportedUser(userId: string): Promise<void> {
        const index = this.reportedContent.userIds.indexOf(userId);
        if (index > -1) {
            this.reportedContent.userIds.splice(index, 1);
            await this.saveReportedContent();
        }
    }

    /**
     * Remove a hashtag from the filter list
     */
    public async removeReportedHashtag(hashtag: string): Promise<void> {
        const cleanHashtag = hashtag.replace('#', '').toLowerCase();
        const index = this.reportedContent.hashtags.indexOf(cleanHashtag);
        if (index > -1) {
            this.reportedContent.hashtags.splice(index, 1);
            await this.saveReportedContent();
        }
    }
}

// Export singleton instance
export const contentFilter = ContentFilterService.getInstance();